import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Comment } from "../../types/comment";
import Avatar from "../Avatar/Avatar";
import CommentInput from "./CommentInput";
import styles from "./CommentSection.module.scss";
import { Trash2, ThumbsUp } from "lucide-react";

interface CommentItemProps {
  comment: Comment;
  onReply: (parentCommentId: string, body: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onLike: (commentId: string, nextLiked: boolean) => Promise<void>;
  currentUserId: string;
  currentUser?: { username: string; avatarUrl?: string; profileImageUrl?: string };
  isReply?: boolean;
}

function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;

  if (diffMs < 60_000) return "Just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;

  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function CommentItem({
  comment,
  onReply,
  onDelete,
  onLike,
  currentUserId,
  currentUser,
  isReply = false,
}: CommentItemProps) {
  const navigate = useNavigate();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const relativeTime = useMemo(
    () => getRelativeTime(comment.createdAt),
    [comment.createdAt]
  );

  const isOwner = currentUserId === comment.authorId;
  const hasReplies = !isReply && comment.replies && comment.replies.length > 0;

  const handleReplySubmit = async (body: string) => {
    await onReply(comment.commentId, body);
    setShowReplyInput(false);
    setShowReplies(true);
  };

  return (
    <div
      className={`${styles.commentItem} ${isReply ? styles.reply : ""}`}
    >
      <Avatar
        src={comment.author.profileImageUrl || comment.author.avatarUrl}
        username={comment.author.username}
        size={isReply ? 28 : 'sm'}
        onClick={() => navigate(`/profile?user=${comment.author.userId}`)}
      />
      <div className={styles.commentContent}>
        <div className={styles.commentHeader}>
          <span className={styles.commentUsername}>
            {comment.author.username}
          </span>
          <span className={styles.commentTimestamp}>{relativeTime}</span>
          {isOwner && (
            <button
              type="button"
              className={styles.deleteButton}
              onClick={() => onDelete(comment.commentId)}
              aria-label="Delete comment"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        <p className={styles.commentBody}>{comment.body}</p>
        <div className={styles.commentActions}>
          <button
            type="button"
            className={`${styles.likeButton} ${comment.likedByMe ? styles.liked : ""}`}
            onClick={() => onLike(comment.commentId, !comment.likedByMe)}
            aria-pressed={comment.likedByMe}
            aria-label={comment.likedByMe ? "Unlike comment" : "Like comment"}
          >
            <ThumbsUp size={14} /> {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
          </button>
          <button
            type="button"
            className={styles.replyButton}
            onClick={() => setShowReplyInput((prev) => !prev)}
          >
            Reply
          </button>
          {hasReplies && (
            <button
              type="button"
              className={styles.toggleRepliesButton}
              onClick={() => setShowReplies((prev) => !prev)}
            >
              {showReplies
                ? "Hide replies"
                : `View ${comment.replies.length} ${comment.replies.length === 1 ? "reply" : "replies"}`}
            </button>
          )}
        </div>
        {showReplyInput && (
          <CommentInput
            onSubmit={handleReplySubmit}
            placeholder="Write a reply..."
            autoFocus
          />
        )}
        {showReplies && hasReplies && (
          <div className={styles.replies}>
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.commentId}
                comment={reply}
                onReply={onReply}
                onDelete={onDelete}
                onLike={onLike}
                currentUserId={currentUserId}
                currentUser={currentUser}
                isReply
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
